import { useSyncExternalStore } from 'react';
import type { BookingItem } from './booking-api';

/**
 * The booking wizard is split across multiple routes (location →
 * service → schedule → addons → professional → continue-booking) and
 * — crucially — across an auth boundary (guest signs up between the
 * wizard and ContinueBookingPage). We hold the inflight draft in
 * localStorage so it survives the round-trip and the page refreshes
 * Stripe Checkout triggers when it lands the customer back on us.
 *
 * Two kinds: ONE_OFF (single appointment) and SUBSCRIPTION (recurring
 * via Stripe Subscription). The shape is intentionally close to the
 * backend's CreateOneOff / CreateSubscription DTOs so the handoff in
 * ContinueBookingPage is a near-pass-through.
 */

export type BookingDraftAddress = {
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type BookingDraft = {
  // Filled in once the customer picks a category + service. For the
  // hardcoded UI mock these may not be set yet — the checkout page
  // resolves the catalog at submit-time.
  service_category_id?: string | null;
  service_id?: string | null;

  // Which UI flow they walked through. Helps the checkout page know
  // whether to send a one-off or a subscription.
  mode: 'ONE_OFF' | 'SUBSCRIPTION';

  // Subscription cadence. Only used when mode === SUBSCRIPTION.
  frequency?: 'DAILY' | 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY';
  day_of_week?: number; // 0 (Sun) – 6 (Sat). Omitted for DAILY.
  start_time?: string; // HH:MM:SS interpreted in `timezone` below
  // IANA timezone the wall-clock pick was made in. Captured via
  // Intl.DateTimeFormat().resolvedOptions().timeZone when the
  // wizard finalises the schedule, then sent to the backend so the
  // spawner can compute scheduled_start in the customer's local
  // zone (audit 2026-06-04, high — UTC drift fix).
  timezone?: string;

  // One-off appointment time. Only used when mode === ONE_OFF.
  scheduled_start?: string; // ISO datetime

  duration_minutes?: number;
  professional_id?: string | null;

  address?: BookingDraftAddress | null;

  items: BookingItem[];

  customer_voucher_id?: string | null;
  wallet_amount?: number;
  customer_note?: string | null;
  // Reference photos attached to "Your expectations concerning the pro".
  // Public S3 URLs uploaded via the anonymous booking-attachment route;
  // passed straight to the booking at checkout.
  customer_note_images?: string[] | null;
  currency?: string;

  // Free-form metadata the wizard wants to remember (the cleaning
  // flow stashes home size / pets / hours / etc. here). Snapshotted
  // onto booking_service_items.properties at checkout-time so the
  // pro sees the same answers the customer gave.
  meta?: Record<string, any>;
};

const STORAGE_KEY = 'cherry.bookingDraft.v1';

const subscribers = new Set<() => void>();

function notify() {
  try {
    for (const s of subscribers) {
      try {
        s();
      } catch (e) {
        // One bad subscriber shouldn't break the others
        console.error('Error in booking draft subscriber:', e);
      }
    }
  } catch (e) {
    console.error('Error notifying booking draft subscribers:', e);
  }
}

// Snapshot cache. `useSyncExternalStore` requires getSnapshot to
// return the *same object reference* when nothing has changed,
// otherwise React reschedules a render in a tight loop and tears the
// process down with "Maximum update depth exceeded" (React error
// #185). Comparing the raw localStorage string lets us re-parse only
// when the underlying value actually changes.
let cachedRaw: string | null | undefined = undefined;
let cachedDraft: BookingDraft | null = null;

function safeRead(): BookingDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedDraft;
    cachedRaw = raw;
    cachedDraft = raw ? (JSON.parse(raw) as BookingDraft) : null;
    return cachedDraft;
  } catch (e) {
    console.error('Error reading booking draft from localStorage:', e);
    cachedRaw = null;
    cachedDraft = null;
    return null;
  }
}

export function getBookingDraft(): BookingDraft | null {
  return safeRead();
}

export function setBookingDraft(draft: BookingDraft | null): void {
  if (typeof window === 'undefined') return;
  if (draft === null) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }
  notify();
}

export function patchBookingDraft(patch: Partial<BookingDraft>): BookingDraft {
  const current = safeRead() ?? { mode: 'ONE_OFF', items: [] };
  const next: BookingDraft = { ...current, ...patch };
  setBookingDraft(next);
  return next;
}

export function clearBookingDraft(): void {
  setBookingDraft(null);
}

/**
 * Where to send the customer to resume an in-flight booking after an
 * auth round-trip (sign-in / sign-up / email verification), when the
 * router `from` state was lost — e.g. the email-verification link is a
 * fresh page load with no React Router state, so the guest used to land
 * on the home page instead of back at checkout. The booking draft itself
 * survives in localStorage, so we route off that. Returns null when no
 * booking is in progress (caller falls back to the landing page).
 */
export function pendingBookingResumePath(): string | null {
  const d = safeRead();
  if (!d) return null;
  const hasBooking = !!(d.service_id || (d.items && d.items.length > 0));
  if (!hasBooking) return null;
  return d.mode === 'SUBSCRIPTION' ? '/continue-booking' : '/your-appointment';
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  // Cross-tab updates — localStorage emits storage events to *other*
  // tabs only, but a setBookingDraft in the same tab already notifies
  // via the in-memory subscriber set above.
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  return () => {
    subscribers.delete(cb);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

/** Subscribe a component to the booking draft store. */
export function useBookingDraft(): BookingDraft | null {
  return useSyncExternalStore(subscribe, getBookingDraft, () => null);
}
